import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import PageBanner from '../components/PageBanner';
import Reveal from '../components/ui/Reveal';
import usePage from '../hooks/usePage';
import { UPLOADS_URL } from '../api/axios';

const resolveImage = (src, fallback = '/images/facilities/Junior-Classroom.jpg') => {
  if (!src) return fallback;
  if (src.startsWith('http://') || src.startsWith('https://') || src.startsWith('/')) return src;
  return `${UPLOADS_URL}/${src}`;
};

const renderParagraphs = (raw, fallback = '') => {
  const text = raw || fallback;
  if (!text) return null;
  const parts = String(text).replace(/\r/g, '').split(/\n\s*\n|\n/).map(s => s.trim()).filter(Boolean);
  return parts.map((p, i) => (
    <p key={i} className="text-body leading-[1.8] text-[15px] md:text-[16px]">
      {p}
    </p>
  ));
};

const OurProgrammesPage = () => {
  const pageData = usePage('our-programmes');
  const sections = pageData.sections || {};

  const banner = sections.banner || pageData.banner || {};
  const bannerTitle = banner.title || pageData.title || "Our School Programmes";
  const bannerSubtitle = banner.subtitle || "Age-Appropriate, Comprehensive Learning Pathways from Foundational to Senior Years";
  const bannerBreadcrumb = banner.breadcrumb || "OUR PROGRAMMES";
  const bannerImgSrc = pageData.bannerImage || '/images/page-banners/curricullam-infrastructure.jpg';

  const intro = sections.intro || pageData.content || "";
  const programmes = sections.programmes || [];

  return (
    <>
      <SEO
        title={pageData.title || "Our Programmes"}
        description="Explore educational programmes at St. Andrews World School, Sonipat."
        keywords="Our Programmes, Junior School, Middle School, Senior School, CBSE Programmes, SAWS Sonipat"
      />

      {/* 1. HERO BANNER */}
      <PageBanner
        title={bannerTitle}
        subtitle={bannerSubtitle}
        breadcrumb={bannerBreadcrumb}
        bgImage={bannerImgSrc}
      />

      {/* 2. OPEN OVERVIEW / INTRO (No Box) */}
      {intro && (
        <section className="bg-white py-14 md:py-20 border-b border-gray-100">
          <div className="container  mx-auto px-5 text-center">
            <Reveal>
              <span className="text-secondary text-xs md:text-sm font-bold tracking-[0.2em] uppercase mb-2 block">
                Continuous Learning
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-primary mb-4">
                Tailored Pathways Across Every Age
              </h2>
              <div className="w-16 h-1 bg-secondary mx-auto mb-6 rounded-full" />
              <div className="space-y-4 mx-auto text-left md:text-center">
                {renderParagraphs(intro)}
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* 3. OPEN PROGRAMMES SECTIONS (Full-width alternating, NO boxes) */}
      {programmes.map((prg, idx) => {
        const isReversed = idx % 2 === 1;
        const prgImg = resolveImage(prg.image);
        const highlightsList = (prg.highlights || '').split('|').map(s => s.trim()).filter(Boolean);
        const isWarmBg = idx % 2 === 1;

        return (
          <section
            key={idx}
            className={`py-16 md:py-24 ${isWarmBg ? 'bg-[#f9f8f4]' : 'bg-white'} border-b border-gray-100`}
          >
            <div className="container max-w-7xl mx-auto px-5 lg:px-8">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
                
                {/* PHOTO COLUMN (No Box) */}
                <div className={`lg:col-span-6 ${isReversed ? 'lg:order-2' : 'lg:order-1'}`}>
                  <Reveal delayMs={isReversed ? 100 : 0}>
                    <div className="relative group">
                      {/* Subtle decorative offset border */}
                      <div className="absolute -inset-2.5 sm:-inset-3.5 rounded-3xl bg-secondary/10 -z-10 transform -rotate-1 group-hover:rotate-0 transition-transform duration-500" />
                      
                      {/* Main Image Frame */}
                      <div className="relative rounded-2xl overflow-hidden aspect-[4/3] bg-gray-100 shadow-md">
                        <img
                          src={prgImg}
                          alt={prg.title || 'Programme Image'}
                          className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-primary/50 via-transparent to-transparent pointer-events-none" />

                        {/* Floating Grade Badge on Image */}
                        {prg.badge && (
                          <div className="absolute top-4 left-4">
                            <span className="px-3.5 py-1.5 rounded-full bg-primary/90 text-white font-bold text-xs backdrop-blur-md shadow-md border border-white/20 inline-flex items-center gap-1.5">
                              <i className="fas fa-graduation-cap text-secondary text-xs" />
                              <span>{prg.badge}</span>
                            </span>
                          </div>
                        )}

                        {/* Division Number Pill */}
                        <div className="absolute bottom-4 right-4 px-3 py-1 rounded-full bg-white/90 backdrop-blur text-primary font-bold text-xs shadow-sm">
                          Stage 0{idx + 1}
                        </div>
                      </div>
                    </div>
                  </Reveal>
                </div>

                {/* EDITORIAL CONTENT COLUMN (No Box) */}
                <div className={`lg:col-span-6 ${isReversed ? 'lg:order-1' : 'lg:order-2'}`}>
                  <Reveal delayMs={isReversed ? 0 : 100}>
                    <div className="space-y-4">
                      
                      {/* Kicker Tag */}
                      <div className="flex items-center gap-2">
                        <span className="text-secondary text-xs md:text-sm font-bold tracking-[0.2em] uppercase">
                          DIVISION 0{idx + 1}
                        </span>
                        {prg.badge && (
                          <>
                            <span className="text-gray-300">•</span>
                            <span className="text-muted text-xs font-semibold uppercase tracking-wider">
                              {prg.badge}
                            </span>
                          </>
                        )}
                      </div>

                      {/* Title */}
                      <h3 className="text-3xl md:text-4xl lg:text-[42px] font-display font-bold text-primary leading-tight">
                        {prg.title}
                      </h3>

                      {/* Lead Tagline */}
                      {prg.lead && (
                        <p className="font-serif italic text-secondary text-base md:text-lg font-medium leading-relaxed">
                          “{prg.lead}”
                        </p>
                      )}

                      <div className="w-16 h-1 bg-secondary rounded-full" />

                      {/* Paragraphs */}
                      <div className="space-y-3 pt-2 text-body">
                        {renderParagraphs(prg.paragraphs)}
                      </div>

                      {/* Highlights as open chips */}
                      {highlightsList.length > 0 && (
                        <div className="pt-2">
                          <div className="flex flex-wrap gap-2 pt-1">
                            {highlightsList.map((hl, hIdx) => (
                              <span
                                key={hIdx}
                                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold bg-white border border-gray-200 text-primary shadow-xs hover:border-secondary transition-colors"
                              >
                                <i className="fas fa-check-circle text-secondary text-xs" />
                                <span>{hl}</span>
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Explore Link / Button */}
                      {prg.link && (
                        <div className="pt-4">
                          <Link
                            to={prg.link}
                            className="inline-flex items-center gap-2.5 px-7 py-3 rounded-full text-xs font-bold uppercase tracking-wider bg-primary hover:bg-secondary text-white transition-all duration-300 shadow-sm hover:shadow-md group"
                          >
                            <span>Explore {prg.title}</span>
                            <i className="fas fa-arrow-right text-xs group-hover:translate-x-1 transition-transform" />
                          </Link>
                        </div>
                      )}

                    </div>
                  </Reveal>
                </div>

              </div>
            </div>
          </section>
        );
      })}
    </>
  );
};

export default OurProgrammesPage;
